import { money } from '@odonix/core';

/**
 * Числа и даты экранов админки. Деньги — в рублях с копейками там, где их
 * сверяют, и компактно («880 тыс. ₽») там, где их сравнивают взглядом.
 */

export function rub(minor: number): string {
  return money.format(money.kopecks(minor));
}

/** Со знаком: «+4 999,00 ₽», «−1 500,00 ₽». Ноль — без знака. */
export function rubSigned(minor: number): string {
  if (minor === 0) return rub(0);
  return `${minor > 0 ? '+' : '−'}${rub(Math.abs(minor))}`;
}

export function rubCompact(minor: number): string {
  const rubles = minor / 100;
  const sign = rubles < 0 ? '−' : '';
  const value = Math.abs(rubles);
  if (value >= 1_000_000) return `${sign}${(value / 1_000_000).toLocaleString('ru-RU', { maximumFractionDigits: 1 })} млн ₽`;
  if (value >= 10_000) return `${sign}${(value / 1_000).toLocaleString('ru-RU', { maximumFractionDigits: 0 })} тыс. ₽`;
  return `${sign}${rub(Math.abs(minor))}`;
}

export function percent(value: number): string {
  return `${value.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} %`;
}

export function count(value: number): string {
  return value.toLocaleString('ru-RU');
}

export function plural(n: number, forms: readonly [string, string, string]): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return forms[0];
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return forms[1];
  return forms[2];
}

/** «14 окт. 2026». */
export function day(date: string): string {
  return new Date(`${date.slice(0, 10)}T00:00:00Z`).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** «14 октября, 18:20» — по Москве. */
export function moment(iso: string): string {
  return new Date(iso).toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Moscow',
  });
}

/** «Октябрь 2026». */
export function monthTitle(month: string): string {
  const text = new Date(`${month.slice(0, 7)}-01T00:00:00Z`).toLocaleDateString('ru-RU', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
  return text.charAt(0).toUpperCase() + text.slice(1).replace(' г.', '');
}

/** «окт» — подпись столбца графика. */
export function monthShort(month: string): string {
  return new Date(`${month.slice(0, 7)}-01T00:00:00Z`)
    .toLocaleDateString('ru-RU', { month: 'short', timeZone: 'UTC' })
    .replace('.', '');
}

/** Рубли из поля формы («4 999,50») в копейки; `null` — не число. */
export function parseRubles(input: string): number | null {
  const normalized = input.replace(/\s/g, '').replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return null;
  return Math.round(Number(normalized) * 100);
}

export function rublesInput(minor: number): string {
  const rubles = minor / 100;
  return Number.isInteger(rubles) ? String(rubles) : rubles.toFixed(2).replace('.', ',');
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}
